import { Button, Group, Modal, Stack, Text, type ButtonProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useAsyncAction } from "../../lib/useAsyncAction";
import { ErrorState } from "./StateView";

interface ConfirmButtonProps extends Omit<ButtonProps, "onClick"> {
  /** Button caption; also the confirm caption in the dialog. */
  label?: string;
  title?: string;
  message: ReactNode;
  onConfirm: () => Promise<void>;
}

/**
 * A destructive action behind a confirmation dialog. Owns the dialog, the busy state and the error
 * banner, so a Delete anywhere in the app is one line. Defaults to the red trash-can "Delete".
 */
export default function ConfirmButton({
  label = "Delete",
  title = "Are you sure?",
  message,
  onConfirm,
  color = "red",
  variant = "light",
  leftSection = <IconTrash size={16} />,
  ...buttonProps
}: ConfirmButtonProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { run, busy, error } = useAsyncAction(async () => {
    await onConfirm();
    close();
  });

  return (
    <>
      <Button color={color} variant={variant} leftSection={leftSection} onClick={open} {...buttonProps}>
        {label}
      </Button>
      <Modal opened={opened} onClose={close} title={title} centered>
        <Stack gap="md">
          <Text size="sm">{message}</Text>
          {error && <ErrorState message={error} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button color={color} loading={busy} onClick={() => void run()}>
              {label}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
